import { useFormContext } from 'react-hook-form';

import { useSmartyAutocomplete } from '@consumer-frontend/nc-api';
import {
  RegistrationAddressT,
  stateOptions,
  validateAddressLine1,
  validateCity,
  validateState,
  validateZip,
} from '@consumer-frontend/nc-utils';

import { NcAddressSuggestions } from '../NcAddressSuggestions';
import { NcInput } from '../NcInput';
import { NcSelect } from '../NcSelect';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/form/address-fields.scss';

type AddressFieldNameT = keyof RegistrationAddressT;

export type NcAddressFieldsPropsT = {
  isForSpouse?: boolean;
};

export const NcAddressFields = ({ isForSpouse }: NcAddressFieldsPropsT): React.JSX.Element => {
  const {
    register,
    getFieldState,
    formState,
    setValue,
    watch,
  } = useFormContext();

  const fieldName = (name: AddressFieldNameT): string => {
    return isForSpouse ? `spouse.address.${name}` : name;
  };

  const fieldError = (name: AddressFieldNameT): string | undefined => {
    return getFieldState(fieldName(name), formState).error?.message;
  };

  // Generate unique IDs to support rendering in the same DOM without conflicts
  const getFieldId = (id: string): string => {
    return isForSpouse ? `spouse-${id}` : id;
  };

  const address = {
    addressLine1: watch(fieldName('addressLine1')) || '',
    addressLine2: watch(fieldName('addressLine2')) || '',
    city: watch(fieldName('city')) || '',
    postalCode: watch(fieldName('postalCode')) || '',
    state: watch(fieldName('state')) || '',
  } as RegistrationAddressT;

  const {
    formContainerRef,
    getSuggestionId,
    highlightedSuggestionIndex,
    onAddressFieldChange,
    onAddressFieldBlur,
    onAddressFieldFocus,
    onAddressFieldKeyDown,
    onSelectSuggestion,
    showSuggestions,
    suggestions,
    suggestionsRef,
    suggestionsStyle,
  } = useSmartyAutocomplete({
    address,
    onAddressChange: (nextAddress) => {
      Object.entries(nextAddress).forEach(([name, value]) => {
        setValue(fieldName(name as AddressFieldNameT), value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    },
  });

  const addressLine1Field = register(fieldName('addressLine1'), {
    validate: validateAddressLine1(isForSpouse),
  });

  return (
    <div
      className="nc-address-fields"
      onBlurCapture={onAddressFieldBlur}
      onChangeCapture={onAddressFieldChange}
      onFocusCapture={onAddressFieldFocus}
      onKeyDownCapture={onAddressFieldKeyDown}
      ref={formContainerRef}
    >
      <div className="address-field">
        <NcInput
          {...addressLine1Field}
          aria-activedescendant={showSuggestions && highlightedSuggestionIndex >= 0
            ? getSuggestionId(highlightedSuggestionIndex)
            : undefined}
          aria-autocomplete="list"
          aria-controls="address-suggestions"
          aria-expanded={showSuggestions}
          autoComplete="address-line1"
          errorMessage={fieldError('addressLine1')}
          id={getFieldId('address-line-1')}
          label="Address Line 1"
          type="text"
          role="combobox"
        />

        {showSuggestions && (
          <NcAddressSuggestions
            getSuggestionId={getSuggestionId}
            highlightedSuggestionIndex={highlightedSuggestionIndex}
            onSelectSuggestion={onSelectSuggestion}
            suggestions={suggestions}
            suggestionsRef={suggestionsRef}
            suggestionsStyle={suggestionsStyle}
          />
        )}
      </div>

      <NcInput
        {...register(fieldName('addressLine2'))}
        autoComplete="address-line2"
        id={getFieldId('address-line-2')}
        label="Address Line 2"
        optional
        type="text"
      />

      <NcInput
        {...register(fieldName('city'), {
          validate: validateCity(isForSpouse),
        })}
        autoComplete="address-level2"
        errorMessage={fieldError('city')}
        id={getFieldId('city')}
        label="City"
        type="text"
      />

      <div className="state-zip-row">
        <NcSelect
          {...register(fieldName('state'), { validate: validateState(isForSpouse) })}
          autoComplete="address-level1"
          errorMessage={fieldError('state')}
          id={getFieldId('state')}
          label="State"
          options={stateOptions}
          placeholder="Select a state"
        />

        <NcInput
          {...register(fieldName('postalCode'), {
            validate: validateZip(isForSpouse),
          })}
          autoComplete="postal-code"
          errorMessage={fieldError('postalCode')}
          id={getFieldId('zip-code')}
          inputMode="numeric"
          label="ZIP Code"
          maxLength={5}
          type="text"
        />
      </div>
    </div>
  );
};
